export const nextSectionIds = ['concepts', 'entities', 'cards'] as const
export type NextSectionId = (typeof nextSectionIds)[number]

export enum NextCardImageFilter {
  All = 'all',
  Missing = 'missing',
}

export interface NextLanguage {
  code: string
  name: string
}

export interface NextConceptOption {
  id: string
  title: string
  kind: string
  cefr: string | null
}

export interface NextCatalogMetadata {
  languages: NextLanguage[]
  concepts: NextConceptOption[]
  conceptKinds: string[]
  entityKinds: string[]
  cefrLevels: string[]
  counts: Record<NextSectionId, number>
}

export interface NextPagination {
  page: number
  pageSize: number
  total: number
  totalPages: number
}

export interface NextPage<T> extends NextPagination {
  items: T[]
}

export interface NextConceptSummary {
  id: string
  kind: string
  title: string
  cefr: string | null
  description: string
  file: string
}

export interface NextEntitySummary {
  id: string
  kind: string
  description: string
  image: string | null
  imageHash: string | null
  file: string
}

export interface NextMediaReference {
  image: string
  label: string | null
  imageHash: string | null
}

export interface NextEntityImage {
  id: string
  image: string | null
  imageHash: string | null
  imageDescription: string
  prompt: string
  references: NextMediaReference[]
}

export const nextRelationGroups = ['family', 'ownership', 'social', 'other'] as const
export type NextRelationGroup = (typeof nextRelationGroups)[number]

export interface NextEntityRelationship {
  relation: string
  relationGroup: NextRelationGroup
  relationName: string
  target: string
  description: string
  targetDescription: string
  targetImage: string | null
  targetImageHash: string | null
}

export interface NextEntityIncomingRelationship {
  source: string
  relation: string
  relationGroup: NextRelationGroup
  relationName: string
  description: string
  sourceDescription: string
  sourceImage: string | null
  sourceImageHash: string | null
}

export interface NextLorePart {
  text: string
  entityId: string | null
  image: string | null
  imageHash: string | null
  description: string | null
}

export interface NextEntityGraphNode {
  id: string
  description: string
  image: string | null
  imageHash: string | null
}

export interface NextEntityGraphEdge {
  source: string
  target: string
  relation: string
  relationGroup: NextRelationGroup
  relationName: string
  description: string
}

export interface NextEntityGraph {
  rootId: string
  nodes: NextEntityGraphNode[]
  edges: NextEntityGraphEdge[]
}

export interface NextDependentCard {
  id: string
  sortOrder: number
  scene: string
  image: string | null
  imageHash: string | null
}

export interface NextEntityDependentCard extends NextDependentCard {
  asReference: boolean
  asMention: boolean
}

export interface NextEntityDetail extends NextEntitySummary {
  imageHash: string | null
  imageDescription: string
  prompt: string
  references: NextMediaReference[]
  loreSummary: string
  loreParts: NextLorePart[]
  relationships: NextEntityRelationship[]
  incomingRelationships: NextEntityIncomingRelationship[]
  additionalImages: NextEntityImage[]
  dependentCards: NextEntityDependentCard[]
}

export interface NextCardImage {
  image: string | null
  imageHash: string | null
  imageDescription: string
  prompt: string
  references: NextMediaReference[]
}

export interface NextCardSummary {
  id: string
  sortOrder: number
  images: NextCardImage[]
  imagesFrom: string | null
  scene: string
  previewText: string | null
  textDescription: string | null
  concepts: string[]
  languageCodes: string[]
  file: string
}

export interface NextCardLanguageText {
  language: NextLanguage
  text: string | null
}

export interface NextCardScenePart {
  text: string
  entityId: string | null
  image: string | null
  imageHash: string | null
  description: string | null
}

export interface NextCardDetail extends NextCardSummary {
  sceneParts: NextCardScenePart[]
  introduces: string[]
  reinforces: string[]
  requires: string[]
  conceptCounts: Record<string, number>
  languages: NextCardLanguageText[]
  dependentCards: NextDependentCard[]
}
